import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ChoiceSaveQueue, currentPrincipal, principalOf } from "_shared/_api/choice-save-queue";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import {
  TableColumnPreferenceSchema,
  type TableColumnPreferenceJSON,
  type TableColumnPreferencesJSON,
  type TableViewStateJSON,
} from "_shared/_views/tables/_schemas/table-column-preferences-schema";
import { tableColumnPreferencesApi } from "_shared/_views/tables/_api/table-column-preferences-api";
import type Session from "users/__session/session";

/**
 * The reader's views of each table, kept on their account (#6714): the view they left a list in,
 * and the views they saved under a name for the Views menu (#6717).
 *
 * Every answer is the table's whole record, and is written into the one cached answer for every
 * table that `getTableColumnPreferences` holds, so a page reads its views from the same place its
 * columns come from.
 */

/** What a view shows, as this app holds it. A column part left out is the stored one, kept. */
export type TableViewState = Readonly<{
  /** The path of the tab on screen, `/proposals/requests`; null for a page with no tabs. */
  tabPath: string | null;
  /** The view's search parameters, without `?`, and without the search text or the page number. */
  search: string;
  columnVisibility?: Readonly<Record<string, boolean>>;
  columnSizing?: Readonly<Record<string, number>>;
  columnOrder?: readonly string[];
}>;

/** The wire form of a view, which carries column choices as lists. */
export function toTableViewStateJSON(state: TableViewState): TableViewStateJSON {
  return {
    tabPath: state.tabPath,
    search: state.search,
    columnVisibility: state.columnVisibility
      ? Object.entries(state.columnVisibility).map(([columnId, visible]) => ({ columnId, visible }))
      : undefined,
    columnSizing: state.columnSizing
      ? Object.entries(state.columnSizing).map(([columnId, width]) => ({ columnId, width }))
      : undefined,
    columnOrder: state.columnOrder ? [...state.columnOrder] : undefined,
  };
}

export function fromTableViewStateJSON(json: TableViewStateJSON): TableViewState {
  return {
    tabPath: json.tabPath ?? null,
    search: json.search ?? "",
    columnVisibility: Object.fromEntries((json.columnVisibility ?? []).map((entry) => [entry.columnId, entry.visible])),
    columnSizing: Object.fromEntries((json.columnSizing ?? []).map((entry) => [entry.columnId, entry.width])),
    columnOrder: json.columnOrder ?? [],
  };
}

type TableArgs = { session: Session; tableKey: string };

export type SaveCurrentTableViewArgs = TableArgs & {
  /** The saved view the current view started from; null for the page's Default view. */
  viewId: string | null;
  state: TableViewState;
};

export type NamedTableViewArgs = TableArgs & { name: string; state: TableViewState };

type RequestAnswer = { data?: unknown; error?: FetchBaseQueryError };
type Send = (args: FetchArgs) => RequestAnswer | PromiseLike<RequestAnswer>;
type Answer = { data: TableColumnPreferenceJSON } | { error: FetchBaseQueryError };

/** What a request bound to someone who is no longer signed in answers, without being sent. */
const PRINCIPAL_CHANGED: FetchBaseQueryError = {
  status: "CUSTOM_ERROR",
  error: "The person who made this change is no longer the one signed in, so it was not sent.",
};

/** The current-view saves on their way, one queue per table: see `ChoiceSaveQueue`. */
const currentViewSaves = new ChoiceSaveQueue<TableViewState>();

function tableUrl(tableKey: string, rest = ""): string {
  return `users/me/table-preferences/${encodeURIComponent(tableKey)}${rest}`;
}

/** Sends a request for the person who asked, and never with someone else's token. */
async function sendFor(session: Session, send: Send, args: FetchArgs): Promise<Answer> {
  if (principalOf(session) !== currentPrincipal()) return { error: PRINCIPAL_CHANGED };
  const result = await send(args);
  return result.error ? { error: result.error } : { data: result.data as TableColumnPreferenceJSON };
}

/** The current view the cache holds for a table: every save of it, and of its columns, is written there first. */
type CachedCurrentView = (tableKey: string) => TableViewStateJSON | null | undefined;

type PreferencesState = Parameters<
  ReturnType<typeof tableColumnPreferencesApi.endpoints.getTableColumnPreferences.select>
>[0];

function cachedCurrentViewOf(state: unknown, session: Session, tableKey: string): TableViewStateJSON | null | undefined {
  return tableColumnPreferencesApi.endpoints.getTableColumnPreferences
    .select({ session })(state as PreferencesState)
    .data?.tables.find((table) => table.tableKey === tableKey)?.currentView?.state;
}

/**
 * The column parts of the saves of each table that were never sent, because a newer save took their
 * place: open a view, change a chip before an older save answers, and the chip's save, which leaves
 * the columns out, is the one sent (#6717 review).
 */
const droppedColumns = new Map<string, TableViewStateJSON>();

function keepDroppedColumns(args: SaveCurrentTableViewArgs): void {
  const sent = toTableViewStateJSON(args.state);
  const before = droppedColumns.get(args.tableKey);
  droppedColumns.set(args.tableKey, {
    columnVisibility: sent.columnVisibility ?? before?.columnVisibility,
    columnSizing: sent.columnSizing ?? before?.columnSizing,
    columnOrder: sent.columnOrder ?? before?.columnOrder,
  });
}

/**
 * The save to send, with each column part a save it took the place of carried, and it leaves out.
 *
 * A part every save left out is still left out, so the server keeps the stored one, as a width save
 * leaves the Show/Hide choice alone. A dropped part is sent as the cached current view has it when
 * this save is sent, not as the dropped save had it: a Show/Hide choice goes by its own endpoint, and
 * one made since the view was opened is newer than the view's (#6717 review).
 */
function withDroppedColumns(args: SaveCurrentTableViewArgs, cached: TableViewStateJSON | null | undefined): TableViewStateJSON {
  const sent = toTableViewStateJSON(args.state);
  const dropped = droppedColumns.get(args.tableKey);
  droppedColumns.delete(args.tableKey);
  if (!dropped) return sent;
  return {
    ...sent,
    columnVisibility: sent.columnVisibility ?? (dropped.columnVisibility && (cached?.columnVisibility ?? dropped.columnVisibility)),
    columnSizing: sent.columnSizing ?? (dropped.columnSizing && (cached?.columnSizing ?? dropped.columnSizing)),
    columnOrder: sent.columnOrder ?? (dropped.columnOrder && (cached?.columnOrder ?? dropped.columnOrder)),
  };
}

/**
 * Sends one current-view save in its table's turn. At module level, so the `try`/`finally` that
 * gives the queue back its place is plain code whatever the endpoint is compiled into.
 */
async function saveCurrentViewInTurn(
  args: SaveCurrentTableViewArgs,
  send: Send,
  cachedCurrentView: CachedCurrentView,
): Promise<Answer | { data: null }> {
  const place = currentViewSaves.join(args.tableKey, args.state, principalOf(args.session));
  try {
    await place.turn;
    // A newer save of this table is waiting behind this one, and it carries the whole view.
    if (!place.isNewest()) {
      keepDroppedColumns(args);
      return { data: null };
    }
    const state = withDroppedColumns(args, cachedCurrentView(args.tableKey));
    return await sendFor(args.session, send, {
      url: tableUrl(args.tableKey, "/current-view"),
      method: "PUT",
      body: { viewId: args.viewId, state },
    });
  } finally {
    place.finish();
  }
}

/** Whether a current-view save of the table is still on its way. */
export function hasPendingCurrentViewSave(tableKey: string): boolean {
  return currentViewSaves.hasPendingSave(tableKey);
}

/**
 * A named-view answer written into the cached record of its table: its saved views, and nothing of
 * the current view the cache holds.
 *
 * The cache's current view and columns are the newest this browser has: every current-view and
 * column save writes them before the server answers. The answer's were read when the server made it,
 * and a current-view save sent while it was on its way is not in it, so taking them would put back
 * the view the reader left (#6717 review). A named-view change touches the current view only to drop
 * a deleted view's id from it, as the server's `DeleteView` does.
 */
function withNamedViews(draft: TableColumnPreferencesJSON, answer: TableColumnPreferenceJSON): void {
  const index = draft.tables.findIndex((each) => each.tableKey === answer.tableKey);
  if (index < 0) {
    draft.tables.push(answer);
    return;
  }
  const cached = draft.tables[index];
  const views = answer.views ?? [];
  const currentView = cached.currentView ?? answer.currentView;
  const viewId = currentView?.viewId;
  const viewStillSaved = !viewId || views.some((view) => view.id === viewId);
  draft.tables[index] = {
    ...cached,
    columns: cached.currentView ? cached.columns : answer.columns,
    currentView: currentView && !viewStillSaved ? { ...currentView, viewId: null } : currentView,
    views,
    updatedAt: answer.updatedAt,
  };
}

/**
 * The current view as the reader now sees it, written into the cached answer before the server has
 * it, so a page that reads it next -- the same list opened again from the side nav -- does not open
 * the older one. A column part the save leaves out keeps the cached one, as the server keeps it.
 */
export function withCurrentView(
  draft: TableColumnPreferencesJSON,
  tableKey: string,
  viewId: string | null,
  state: TableViewState,
): void {
  const index = draft.tables.findIndex((table) => table.tableKey === tableKey);
  const existing = index >= 0 ? draft.tables[index] : undefined;
  const stored = existing?.currentView?.state;
  const sent = toTableViewStateJSON(state);
  const merged: TableViewStateJSON = {
    tabPath: sent.tabPath,
    search: sent.search,
    columnVisibility: sent.columnVisibility ?? stored?.columnVisibility ?? existing?.columns ?? [],
    columnSizing: sent.columnSizing ?? stored?.columnSizing ?? [],
    columnOrder: sent.columnOrder ?? stored?.columnOrder ?? [],
  };
  const updatedAt = new Date().toISOString();
  const currentView = { viewId, state: merged, updatedAt };
  if (existing) {
    draft.tables[index] = { ...existing, columns: merged.columnVisibility ?? [], currentView };
    return;
  }
  draft.tables.push({ tableKey, columns: merged.columnVisibility ?? [], currentView, views: [], updatedAt });
}

type CacheDispatch = (action: unknown) => unknown;

/** Writes a table's answer into the cached answer for every table, once the server has it. */
async function writeAnswer(
  session: Session,
  queryFulfilled: Promise<{ data: TableColumnPreferenceJSON | null }>,
  dispatch: CacheDispatch,
): Promise<void> {
  let answer: TableColumnPreferenceJSON | null;
  try {
    answer = (await queryFulfilled).data;
  } catch {
    // The failure is the caller's to report, through `unwrap`.
    return;
  }
  if (!answer) return;
  const table = answer;
  dispatch(
    tableColumnPreferencesApi.util.updateQueryData("getTableColumnPreferences", { session }, (draft) =>
      withNamedViews(draft, table),
    ),
  );
}

export const tableViewsApi = tableColumnPreferencesApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Keeps the view the reader is looking at. Send it only when the reader changed something,
     * never when they arrived by a link: a link is shown, and is not a choice.
     */
    saveCurrentTableView: build.mutation<TableColumnPreferenceJSON | null, SaveCurrentTableViewArgs>({
      queryFn: (args, { getState }, _extraOptions, baseQuery) =>
        saveCurrentViewInTurn(args, baseQuery, (tableKey) => cachedCurrentViewOf(getState(), args.session, tableKey)),
      onQueryStarted: ({ session, tableKey, viewId, state }, { dispatch }) => {
        // Not the answer: a column save of the same table may be on its way, and the answer to an
        // older current-view save would put back what the reader just changed.
        dispatch(
          tableColumnPreferencesApi.util.updateQueryData("getTableColumnPreferences", { session }, (draft) =>
            withCurrentView(draft, tableKey, viewId, state),
          ),
        );
      },
    }),
    createTableView: build.mutation<TableColumnPreferenceJSON, NamedTableViewArgs>({
      queryFn: ({ session, tableKey, name, state }, _api, _extraOptions, baseQuery) =>
        sendFor(session, baseQuery, {
          url: tableUrl(tableKey, "/views"),
          method: "POST",
          body: { name, state: toTableViewStateJSON(state) },
        }),
      responseSchema: TableColumnPreferenceSchema,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: ({ session }, { dispatch, queryFulfilled }) => writeAnswer(session, queryFulfilled, dispatch),
    }),
    updateTableView: build.mutation<TableColumnPreferenceJSON, NamedTableViewArgs & { viewId: string }>({
      queryFn: ({ session, tableKey, viewId, name, state }, _api, _extraOptions, baseQuery) =>
        sendFor(session, baseQuery, {
          url: tableUrl(tableKey, `/views/${encodeURIComponent(viewId)}`),
          method: "PUT",
          body: { name, state: toTableViewStateJSON(state) },
        }),
      responseSchema: TableColumnPreferenceSchema,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: ({ session }, { dispatch, queryFulfilled }) => writeAnswer(session, queryFulfilled, dispatch),
    }),
    deleteTableView: build.mutation<TableColumnPreferenceJSON, TableArgs & { viewId: string }>({
      queryFn: ({ session, tableKey, viewId }, _api, _extraOptions, baseQuery) =>
        sendFor(session, baseQuery, { url: tableUrl(tableKey, `/views/${encodeURIComponent(viewId)}`), method: "DELETE" }),
      responseSchema: TableColumnPreferenceSchema,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: ({ session }, { dispatch, queryFulfilled }) => writeAnswer(session, queryFulfilled, dispatch),
    }),
    reorderTableViews: build.mutation<TableColumnPreferenceJSON, TableArgs & { viewIds: readonly string[] }>({
      queryFn: ({ session, tableKey, viewIds }, _api, _extraOptions, baseQuery) =>
        sendFor(session, baseQuery, { url: tableUrl(tableKey, "/view-order"), method: "PUT", body: { viewIds } }),
      responseSchema: TableColumnPreferenceSchema,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: ({ session }, { dispatch, queryFulfilled }) => writeAnswer(session, queryFulfilled, dispatch),
    }),
    /** Stars the view a plain visit to the list opens; `viewId: null` stars none. */
    starTableView: build.mutation<TableColumnPreferenceJSON, TableArgs & { viewId: string | null }>({
      queryFn: ({ session, tableKey, viewId }, _api, _extraOptions, baseQuery) =>
        sendFor(session, baseQuery, { url: tableUrl(tableKey, "/starred-view"), method: "PUT", body: { viewId } }),
      responseSchema: TableColumnPreferenceSchema,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: ({ session }, { dispatch, queryFulfilled }) => writeAnswer(session, queryFulfilled, dispatch),
    }),
  }),
});

export const {
  useCreateTableViewMutation,
  useDeleteTableViewMutation,
  useReorderTableViewsMutation,
  useSaveCurrentTableViewMutation,
  useStarTableViewMutation,
  useUpdateTableViewMutation,
} = tableViewsApi;
